import {AttrsSchema, Component} from 'panel';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {KEY_VALUES} from 'common/util/dom';
import {ItemClickEvent, Section} from 'common/components/items-menu/types';
import {Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';

import '../tag';
import css from './index.inline.styl';
import {template} from './template';
import {TagItem} from './tag-item';
import {TagInputChangeAction, TagInputJsxProps, TagInputSearchMode, TagInputState, TagInputTheme} from './types';

const MIN_INPUT_WIDTH = `12px`;

function getTagId(tag: TagItem): string {
  return tag.value ?? tag.label;
}

export class TagInput extends Component<TagInputState, TagInputJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<TagInputJsxProps['attrs']> {
    return {
      error: `boolean`,
      'min-tags': {type: `number`, default: 1},
      'show-search-icon': `boolean`,
      'show-clear': `boolean`,
      tags: {
        type: `json`,
        default: [],
        description: `interface: TagItem[], with a label and optional isDisabled
        and attrs params (attrs gets passed down to the mp-tag attrs)`,
      },
      placeholder: `string`,
      'disable-input': `boolean`,
      'mp-select-attrs': `json`,
      'mp-button-attrs': `json`,
      theme: {type: `string`, default: TagInputTheme.Default},
      'search-mode': {type: `string`, default: TagInputSearchMode.Text},
      'mp-items-menu-attrs': `json`,
    };
  }

  get config() {
    return {
      defaultState: {
        isInputFocused: false,
        inputText: ``,
        inputWidth: MIN_INPUT_WIDTH,
        tagMap: new Map(),
      },
      css,
      template,
      helpers: {
        onClearInput: (ev) => {
          ev.stopPropagation();
          this.update({
            inputText: ``,
            inputWidth: MIN_INPUT_WIDTH,
            tagMap: new Map(),
          });
          this.dispatchEvent(new CustomEvent(`clear`));
          this.focus();
        },
        processMpSelectAttrs: () => {
          return this.attr(`mp-select-attrs`);
        },
        onSelectMenuChange: (ev) => {
          this.update({isInputFocused: ev.detail.isOpen});
        },
        onSelectChange: (ev) => {
          this.dispatchEvent(new CustomEvent(`optionSelected`, {detail: ev.detail}));
        },
        processMpButtonAttrs: () => {
          return this.attr(`mp-button-attrs`);
        },
        onButtonPress: (ev) => {
          this.dispatchEvent(new CustomEvent(`buttonPressed`, {detail: ev.detail}));
        },
        onFocusChange: (ev) => {
          this.update({isInputFocused: ev.type === `focus`});
        },
        canRemoveTag: (tag) =>
          this.attr(`theme`) === TagInputTheme.Breadcrumbs ||
          (this.state.tagMap.size > this.attr(`min-tags`) && !tag.isDisabled),
        setInputText: (ev) => {
          const inputText = ev.target.value;
          this._resizeInput(inputText);
          this.changeInputText(inputText);
        },
        removeTag: (tagName) => () => this.changeTags(TagInputChangeAction.RemoveTag, tagName),
        focusInput: () => this.focus(),
        handleKeyDown: (ev) => {
          // ev.keyCode is deprecated
          if (ev.code === KEY_VALUES.BACKSPACE && this.state.inputText === ``) {
            const tagList = Array.from(this.state.tagMap.values());
            for (let i = tagList.length - 1; i >= 0; i--) {
              if (this.config.helpers.canRemoveTag(tagList[i])) {
                this.changeTags(TagInputChangeAction.RemoveTag, tagList[i]);
                break;
              }
            }
          }
        },
        getPlaceholder: () => {
          if (this.state.tagMap.size) {
            return ``;
          }
          return this.attr(`placeholder`) || ``;
        },
      },
      useShadowDom: true,
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `tags`) {
      this.update({
        tagMap: new Map(this.attr(`tags`).map((tag) => [getTagId(tag), tag])),
        // if the tag menu is managed by the parent, clear the input text
        // when tags change for backwards compatibility
        ...(!this.state.tagMenu && {inputText: ``}),
      });
    }
  }

  focus() {
    const searchInput: HTMLInputElement = this.el.querySelector(`.mp-tag-input-search-input`);
    if (searchInput) {
      searchInput.focus();
    }
  }

  set value(value: string) {
    this._resizeInput(value);
    this.update({inputText: value});
  }

  set tagMenuSections(sections: Array<Section<TagItem>>) {
    if (isEqual(this.state.tagMenu?.sections, sections)) {
      return;
    }

    const tagMenu = {
      ...(this.state.tagMenu ?? {isOpen: false}),
      sections,
    };
    this.update({tagMenu});
  }

  getTagMenuSections(): Array<Section<TagItem>> {
    return this.state.tagMenu.sections.map((section) => {
      return {
        ...section,
        items: section.items.map((item) => {
          return {
            // before item destructuring to allow callsite to override elref
            elref: `mpTagInputTagMenuItem`,
            ...item,
            isDisabled: this.state.tagMap.has(getTagId(item.value)),
          };
        }),
      };
    });
  }

  _resizeInput(inputText: string) {
    // make the search input width dynamic
    const span = document.createElement(`span`);
    span.className = `mp-tag-input-search-input-dummy`;
    span.innerText = inputText;
    this.el.appendChild(span);
    const buffer = 24;
    this.update({inputWidth: span.offsetWidth + buffer + `px`});
    this.el.removeChild(span);
  }

  changeTags(action: TagInputChangeAction.AddTag | TagInputChangeAction.RemoveTag, tag: TagItem): void {
    const tagMap = this.state.tagMap;
    if (action === TagInputChangeAction.AddTag) {
      tagMap.set(getTagId(tag), tag);
    } else {
      tagMap.delete(getTagId(tag));
    }
    this.update({tagMap});

    this.dispatchEvent(new CustomEvent(`change`, {detail: {tag, action}}));
    requestAnimationFrame(() => this.focus());
  }

  getStagedTagSearch(): string | null {
    if (this.attr(`search-mode`) === TagInputSearchMode.Text) {
      return this.state.inputText;
    } else {
      const lastWord = last(this.state.inputText.split(/\s+/));
      return lastWord && lastWord.startsWith(`#`) ? lastWord.slice(1) : null;
    }
  }

  handleSearchInputInsert({elm}: {elm: Node}) {
    this.update({searchInputEl: elm as HTMLElement});
  }

  setTagMenuOpen(isOpen: boolean): void {
    this.update({tagMenu: {...this.state.tagMenu, isOpen}});
  }

  handleTagMenuChange(ev: VisibilityChangeEvent): void {
    if (ev.detail.state === Visibility.Closed) {
      this.setTagMenuOpen(false);
    }
    ev.stopPropagation();
    this.dispatchEvent(new CustomEvent(`tagMenuChange`, ev));
  }

  handleClickTagItem(ev: ItemClickEvent<TagItem>) {
    const {item} = ev.detail;
    const tag = item.value;
    this.changeTags(TagInputChangeAction.AddTag, tag);

    let updatedInputText: string;
    if (this.attr(`search-mode`) === TagInputSearchMode.Text) {
      updatedInputText = ``;
    } else {
      updatedInputText = this.state.inputText.replace(
        // remove tag at the very end of searchFilter string
        new RegExp(`#${this.getStagedTagSearch()}$`),
        ``,
      );
    }
    this.changeInputText(updatedInputText);
    this.setTagMenuOpen(false);
    this.state.searchInputEl.focus();
  }

  changeInputText(inputText: string): void {
    this.update({inputText});
    if (this.state.tagMenu) {
      this.setTagMenuOpen(typeof this.getStagedTagSearch() === `string`);
    }
    this.dispatchEvent(
      new CustomEvent(`change`, {detail: {value: this.state.inputText, action: TagInputChangeAction.Text}}),
    );
  }
}

export const TagInputEl = defineAndMakeJsxFactory<TagInputJsxProps>(`mp-tag-input`, TagInput);
export const TagInputReact = panel2react<TagInputJsxProps>(`mp-tag-input`, TagInput);
